import { adminAuthService } from "./adminAuthService";
import apiClient from "./apiClient";

function adminHeaders() {
  return { Authorization: `Bearer ${adminAuthService.getAdminToken()}` };
}

export const adminService = {
  // Dashboard
  async getDashboardStats() {
    const [users, groups, menus] = await Promise.all([
      apiClient.get("/api/admin/users", { headers: adminHeaders() }),
      apiClient.get("/api/admin/groups", { headers: adminHeaders() }),
      apiClient.get("/api/admin/menu-items", { headers: adminHeaders() }),
    ]);
    return {
      totalUsers: users.data.total_count || users.data.users?.length || 0,
      activeGroups: groups.data.length || 0,
      menuItems: menus.data.length || 0,
    };
  },

  // Users
  async getUsers() {
    const r = await apiClient.get("/api/admin/users", { headers: adminHeaders() });
    return r.data.users || [];
  },
  async getUserGroups(userId) {
    const r = await apiClient.get(`/api/admin/users/${userId}/groups`, { headers: adminHeaders() });
    return r.data.groups || [];
  },
  async updateUserGroups(userId, groupIds) {
    const r = await apiClient.put(`/api/admin/users/${userId}/groups`, { group_ids: groupIds }, { headers: adminHeaders() });
    return r.data;
  },
  async createUser(username, email, password, groups = []) {
    const r = await apiClient.post("/api/admin/users",
      { username, email, password, groups },
      { headers: adminHeaders() }
    );
    return r.data;
  },
  async editUser(username, email, newPassword) {
    const payload = {};
    if (email) payload.email = email;
    if (newPassword) payload.new_password = newPassword;
    const r = await apiClient.patch(`/api/admin/users/${username}`, payload, { headers: adminHeaders() });
    return r.data;
  },
  async deleteUser(username) {
    const r = await apiClient.delete(`/api/admin/users/${username}`, { headers: adminHeaders() });
    return r.data;
  },

  // Groups
  async getGroups() {
    const r = await apiClient.get("/api/admin/groups", { headers: adminHeaders() });
    return r.data || [];
  },
  async createGroup(name, description) {
    const r = await apiClient.post("/api/admin/groups", { name, description }, { headers: adminHeaders() });
    return r.data;
  },
  async updateGroup(groupId, name, description) {
    const r = await apiClient.put(`/api/admin/groups/${groupId}`, { name, description }, { headers: adminHeaders() });
    return r.data;
  },
  async deleteGroup(groupId) {
    const r = await apiClient.delete(`/api/admin/groups/${groupId}`, { headers: adminHeaders() });
    return r.data;
  },
  async getGroupUsers(groupId) {
    const r = await apiClient.get(`/api/admin/groups/${groupId}/users`, { headers: adminHeaders() });
    return r.data.users || [];
  },

  // Menu items
  async getMenuItems() {
    const r = await apiClient.get("/api/admin/menu-items", { headers: adminHeaders() });
    return r.data || [];
  },
  async getGroupMenuPermissions(groupId) {
    const r = await apiClient.get(`/api/admin/groups/${groupId}/menu-permissions`, { headers: adminHeaders() });
    return r.data.menu_permissions || [];
  },
  async updateGroupMenuPermissions(groupId, menuIds) {
    const r = await apiClient.put(`/api/admin/groups/${groupId}/menu-permissions`, { menu_ids: menuIds }, { headers: adminHeaders() });
    return r.data;
  },

  // Security
  async getSecuritySummary(days = 7) {
    const r = await apiClient.get(`/api/admin/security-summary?days=${days}`, { headers: adminHeaders() });
    return r.data;
  },
  async getAuditLogs(params = {}) {
    const r = await apiClient.get("/api/admin/audit-logs", { headers: adminHeaders(), params });
    return r.data.logs || [];
  },
  async getActiveSessions() {
    const r = await apiClient.get("/api/admin/active-sessions", { headers: adminHeaders() });
    return r.data.active_sessions || [];
  },
  async getSuspiciousActivity() {
    const r = await apiClient.get("/api/admin/suspicious-activity", { headers: adminHeaders() });
    return r.data;
  },
  async revokeAccess(adminUserId, reason) {
    const r = await apiClient.post(`/api/admin/revoke-access/${adminUserId}`, null, {
      headers: adminHeaders(),
      params: { reason },
    });
    return r.data;
  },

  // Usage Analytics
  async getAnalytics() {
    const r = await apiClient.get("/api/admin/analytics/summary", { headers: adminHeaders() });
    return r.data;
  },

  async seedMenus() {
    const r = await apiClient.post("/api/admin/setup/seed-menus", {}, { headers: adminHeaders() });
    return r.data;
  },

  // Admin users
  async getAdminUsers() {
    const r = await apiClient.get("/api/admin/admin-users", { headers: adminHeaders() });
    return r.data.admin_users || [];
  },
  async createAdminUser(email, displayName, password, adminLevel) {
    const r = await apiClient.post("/api/admin/admin-users",
      { email, display_name: displayName, password, admin_level: adminLevel },
      { headers: adminHeaders() }
    );
    return r.data;
  },
  async deactivateAdminUser(adminUserId) {
    const r = await apiClient.delete(`/api/admin/admin-users/${adminUserId}`, { headers: adminHeaders() });
    return r.data;
  },
  async reactivateAdminUser(adminUserId) {
    const r = await apiClient.post(`/api/admin/admin-users/${adminUserId}/reactivate`, {}, { headers: adminHeaders() });
    return r.data;
  },

  // Menu item CRUD
  async createMenuItem(name, path, icon, order) {
    const r = await apiClient.post("/api/admin/menu-items",
      { name, path, icon, order },
      { headers: adminHeaders() }
    );
    return r.data;
  },
  async deleteMenuItem(menuId) {
    const r = await apiClient.delete(`/api/admin/menu-items/${menuId}`, { headers: adminHeaders() });
    return r.data;
  },

  // User Memory
  async getUserMemory(username) {
    const r = await apiClient.get(`/api/admin/users/${username}/memory`, { headers: adminHeaders() });
    return r.data;
  },
  async clearUserMemory(username) {
    const r = await apiClient.delete(`/api/admin/users/${username}/memory`, { headers: adminHeaders() });
    return r.data;
  },
};
